import { GBasePass, GetBasePassUniformParametersList, Material, Mesh } from "../../../basePass";

import { CreateBuffer, DefineVertexAttrib } from "../../../core/resourcesUtils";
import { Shader, ShaderTF } from "../../../core/shaderUtils";
import { GTexturePool } from "../../../core/texturePool";
import { GetVec2 } from "../../../core/types";
import { GTime } from "../../../core/utils";
import { GLBindUniformBuffer, GLSetTexture, GLSetVec3 } from "../../../helpers/glHelper";
import {
    GetDestParticleUpdateShaderVS,
    GetShaderSourceBasePassParticlePS,
    GetShaderSourceBasePassParticleRenderVS,
    GetShaderSourceBasePassSmokeParticlePS,
    GetShaderSourceBasePassSmokeParticleRenderVS,
} from "../../../shaders/shaderNewParticles";
import { ParticleUpdatePS } from "../../../shaders/shaderParticles";
import { SceneBase } from "../../scene";
import { GBulletSystem } from "./bullets";
import { DestructionTiles } from "./destrTiles";
import { PayneSceneDestrPainting } from "./destrPaintingMain";
import { ParticleDesc } from "./particleDesc";

export class DestructionParticles {
    //Desc
    Desc;

    LastSpawnTime = 0;
    CurSpawnHeadIndex = 0;

    //Resources
    PositionsBufferGPU: WebGLBuffer[] = [];
    VelocitiesBufferGPU: WebGLBuffer[] = [];
    AgeBufferGPU: WebGLBuffer[] = [];
    TransformFeedbackGPU: WebGLTransformFeedback[] = [];
    CurBufferIndex = 0;

    Material = new Material();
    Mesh: Mesh | null = null;

    VAOUpdate: WebGLVertexArrayObject[] = [];
    VAORender: WebGLVertexArrayObject[] = [];

    //Shaders
    ShaderUpdate: ShaderTF | null = null;
    ShaderRender: Shader | null = null;

    AttribToIndexMap = {
        Position: 0,
        Velocity: 1,
        Age: 2,
        VBPositions: 3,
        VBTexCoords: 4,
        VBNormals: 5,
    };

    OnUpdate(
        gl: WebGL2RenderingContext,
        parentTiles: DestructionTiles,
        rndNumSeed: number,
        bSpawnParticlesThisFrame = GBulletSystem.GInstance!.bIntersectionThisFrame,
    ) {
        const spawnRange = GetVec2(-1, -1);

        if (bSpawnParticlesThisFrame) {
            this.LastSpawnTime = GTime.Cur;

            spawnRange.x = this.CurSpawnHeadIndex;
            spawnRange.y = this.CurSpawnHeadIndex + this.Desc.NumParticlesPerSpawn;

            this.CurSpawnHeadIndex += this.Desc.NumParticlesPerSpawn;
            if (this.CurSpawnHeadIndex > this.Desc.MaxNumInstances - 1) {
                this.CurSpawnHeadIndex = 0;
            }
        }

        //Update particles
        {
            const curShader = this.ShaderUpdate!;
            gl.bindFramebuffer(gl.FRAMEBUFFER, null);
            gl.bindRenderbuffer(gl.RENDERBUFFER, null);

            gl.enable(gl.RASTERIZER_DISCARD);

            gl.useProgram(curShader!.ShaderProgram);
            gl.bindVertexArray(this.VAOUpdate[this.CurBufferIndex]);

            const uniformLoc = curShader.UniformParametersLocationList;

            //Set Spawn Range
            gl.uniform2i(uniformLoc.ParticleCurSpawnIndexRange, spawnRange.x, spawnRange.y);
            gl.uniform1f(uniformLoc.RandNumSeed, rndNumSeed);

            GLBindUniformBuffer(
                gl,
                SceneBase.UniformBuffer.BufferGPU!,
                curShader.ShaderProgram,
                uniformLoc.SceneUniformBuffer,
            );

            let textureBindIndex = 1;
            GLSetTexture(
                gl,
                uniformLoc.TileHealthStateTexture,
                parentTiles.TileHealthStateTexture[parentTiles.CurPingPongTextureIndex],
                textureBindIndex++,
            );

            gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, this.TransformFeedbackGPU[1 - this.CurBufferIndex]);

            gl.beginTransformFeedback(gl.POINTS);
            gl.drawArrays(gl.POINTS, 0, this.Desc.MaxNumInstances);
            gl.endTransformFeedback();
            gl.disable(gl.RASTERIZER_DISCARD);

            gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, null);

            this.CurBufferIndex = 1 - this.CurBufferIndex;
        }
    }

    Render(gl: WebGL2RenderingContext, rndNumSeed: number) {
        if (this.Desc.bProceduralCube || this.Desc.bUsesMesh) {
            // Enable back face culling
            gl.enable(gl.CULL_FACE);
            if (this.Desc.bProceduralCube) {
                gl.cullFace(gl.BACK);
            } else {
                gl.cullFace(gl.BACK);
            }
        }

        if (this.Desc.bSmokePath && this.Desc.bEnableBlend) {
            gl.enable(gl.BLEND);
            gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
            //gl.blendFunc(gl.ONE, gl.ONE);
            gl.blendEquation(gl.FUNC_ADD);
            gl.depthMask(false);
        }

        const curShader = this.ShaderRender!;
        gl.useProgram(curShader.ShaderProgram);
        gl.bindVertexArray(this.VAORender[this.CurBufferIndex]);

        const uniformsLocation = curShader.UniformParametersLocationList;

        GLBindUniformBuffer(
            gl,
            SceneBase.UniformBuffer.BufferGPU!,
            curShader.ShaderProgram,
            uniformsLocation.SceneUniformBuffer,
        );

        {
            let textureBindIndex = 1;
            gl.uniform1f(uniformsLocation.TextureScale, this.Material.TextureScale);
            gl.uniform1f(uniformsLocation.Brightness, this.Material.Brightness);
            gl.uniform1f(uniformsLocation.Metalness, this.Material.Metalness);
            gl.uniform1i(uniformsLocation.bEmitter, this.Material.bisEmmiter ? 1 : 0);

            GLSetTexture(gl, uniformsLocation.AlbedoTexture, this.Material.AlbedoTexture, textureBindIndex++);
            /* GLSetTexture(gl, uniformsLocation.NormalTexture, this.Material.NormalTexture, textureBindIndex++);
            GLSetTexture(gl, uniformsLocation.RoughnessTexture, this.Material.RoughnessTexture, textureBindIndex++); */
            GLSetTexture(gl, uniformsLocation.MaskTexture, this.Material.MaskTexture, textureBindIndex++);

            if (SceneBase.TextureEnvMapWithMips) {
                gl.activeTexture(gl.TEXTURE0 + textureBindIndex);
                gl.bindTexture(gl.TEXTURE_CUBE_MAP, SceneBase.TextureEnvMapWithMips);
                gl.uniform1i(uniformsLocation.CubemapTexture, textureBindIndex);
                textureBindIndex++;
            }
            GLSetTexture(gl, uniformsLocation.BRDF_LUT, GBasePass.GInstance!.TextureIntegratedBRDF, textureBindIndex++);
        }

        gl.uniform1f(uniformsLocation.RandNumSeed, rndNumSeed);

        const numVerts = this.Desc.bProceduralCube ? 36 : 6;

        if (this.Mesh) {
            if (this.Mesh.bCanDraw) {
                GLSetVec3(gl, uniformsLocation.PreTranslate, this.Mesh.InitBBoxCenter);
                if (this.Mesh.NumIndices > 0) {
                    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.Mesh.IndexBufferGPU);
                    gl.drawElementsInstanced(
                        gl.TRIANGLES,
                        this.Mesh.NumIndices,
                        this.Mesh.IndexBufferElementType,
                        0,
                        this.Desc.MaxNumInstances,
                    );
                    //gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null);
                } else {
                    gl.drawArraysInstanced(gl.TRIANGLES, 0, this.Mesh.NumVertices, this.Desc.MaxNumInstances);
                }
            }
        } else {
            gl.drawArraysInstanced(gl.TRIANGLES, 0, numVerts, this.Desc.MaxNumInstances);
        }

        if (this.Desc.bProceduralCube || this.Desc.bUsesMesh) {
            gl.disable(gl.CULL_FACE);
        }

        if (this.Desc.bSmokePath && this.Desc.bEnableBlend) {
            gl.disable(gl.BLEND);
            gl.depthMask(true);
        }
    }

    constructor(
        gl: WebGL2RenderingContext,
        scene: PayneSceneDestrPainting,
        parentTiles: DestructionTiles,
        inDesc: ParticleDesc,
    ) {
        this.Desc = inDesc;

        this.Material.bisEmmiter = inDesc.bEmitter;

        if (inDesc.TexNameAlbedo) {
            this.Material.AlbedoTexture = GTexturePool.CreateTexture(
                gl,
                false,
                inDesc.TexNameAlbedo,
                true,
                true,
                false,
            );
        }
        if (inDesc.TexNameMask) {
            this.Material.MaskTexture = GTexturePool.CreateTexture(gl, false, inDesc.TexNameMask, true, true, false);
        }
        if (inDesc.TexNameNormal) {
            this.Material.NormalTexture = GTexturePool.CreateTexture(
                gl,
                false,
                inDesc.TexNameNormal,
                true,
                true,
                false,
            );
        }
        if (inDesc.TexNameRoughness) {
            this.Material.RoughnessTexture = GTexturePool.CreateTexture(
                gl,
                false,
                inDesc.TexNameRoughness,
                true,
                true,
                false,
            );
        }

        //Initialize Resources
        {
            //Load Mesh
            if (inDesc.MeshFilePath && inDesc.bUsesMesh) {
                this.Mesh = new Mesh();
                this.Mesh.bNormalizeScaleAllAxes = true;
                this.Mesh.LoadMesh(gl, inDesc.MeshFilePath, 0, null, false);
                this.Mesh.Material = this.Material;
            }

            const ageBufferCPU = new Float32Array(this.Desc.MaxNumInstances);
            ageBufferCPU.fill(this.Desc.ParticleLifeTimeSec + 1);

            for (let i = 0; i < 2; i++) {
                this.PositionsBufferGPU[i] = CreateBuffer(
                    gl,
                    this.Desc.MaxNumInstances * 3 * Float32Array.BYTES_PER_ELEMENT,
                    gl.STREAM_DRAW,
                );
                this.VelocitiesBufferGPU[i] = CreateBuffer(
                    gl,
                    this.Desc.MaxNumInstances * 3 * Float32Array.BYTES_PER_ELEMENT,
                    gl.STREAM_DRAW,
                );
                this.AgeBufferGPU[i] = CreateBuffer(gl, ageBufferCPU, gl.STREAM_DRAW);

                //Update VAO
                {
                    this.VAOUpdate[i] = gl.createVertexArray()!;
                    gl.bindVertexArray(this.VAOUpdate[i]);
                    {
                        DefineVertexAttrib(
                            gl,
                            this.AttribToIndexMap.Position,
                            this.PositionsBufferGPU[i],
                            3,
                            gl.FLOAT,
                            3 * Float32Array.BYTES_PER_ELEMENT,
                        );
                        DefineVertexAttrib(
                            gl,
                            this.AttribToIndexMap.Velocity,
                            this.VelocitiesBufferGPU[i],
                            3,
                            gl.FLOAT,
                            3 * Float32Array.BYTES_PER_ELEMENT,
                        );
                        DefineVertexAttrib(
                            gl,
                            this.AttribToIndexMap.Age,
                            this.AgeBufferGPU[i],
                            1,
                            gl.FLOAT,
                            1 * Float32Array.BYTES_PER_ELEMENT,
                        );
                    }
                }

                this.TransformFeedbackGPU[i] = gl.createTransformFeedback()!;
                gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, this.TransformFeedbackGPU[i]);
                gl.bindBufferBase(
                    gl.TRANSFORM_FEEDBACK_BUFFER,
                    this.AttribToIndexMap.Position,
                    this.PositionsBufferGPU[i],
                );
                gl.bindBufferBase(
                    gl.TRANSFORM_FEEDBACK_BUFFER,
                    this.AttribToIndexMap.Velocity,
                    this.VelocitiesBufferGPU[i],
                );
                gl.bindBufferBase(gl.TRANSFORM_FEEDBACK_BUFFER, this.AttribToIndexMap.Age, this.AgeBufferGPU[i]);
                gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, null);

                //Render VAO
                {
                    this.VAORender[i] = gl.createVertexArray()!;
                    gl.bindVertexArray(this.VAORender[i]);
                    DefineVertexAttrib(
                        gl,
                        this.AttribToIndexMap.Position,
                        this.PositionsBufferGPU[i],
                        3,
                        gl.FLOAT,
                        3 * Float32Array.BYTES_PER_ELEMENT,
                    );
                    gl.vertexAttribDivisor(this.AttribToIndexMap.Position, 1);

                    DefineVertexAttrib(
                        gl,
                        this.AttribToIndexMap.Velocity,
                        this.VelocitiesBufferGPU[i],
                        3,
                        gl.FLOAT,
                        3 * Float32Array.BYTES_PER_ELEMENT,
                    );
                    gl.vertexAttribDivisor(this.AttribToIndexMap.Velocity, 1);

                    DefineVertexAttrib(
                        gl,
                        this.AttribToIndexMap.Age,
                        this.AgeBufferGPU[i],
                        1,
                        gl.FLOAT,
                        1 * Float32Array.BYTES_PER_ELEMENT,
                    );
                    gl.vertexAttribDivisor(this.AttribToIndexMap.Age, 1);

                    //Mesh Attribs
                    if (inDesc.bUsesMesh && this.Mesh) {
                        DefineVertexAttrib(
                            gl,
                            this.AttribToIndexMap.VBPositions,
                            this.Mesh.VertexBufferGPU!,
                            3,
                            gl.FLOAT,
                            3 * Float32Array.BYTES_PER_ELEMENT,
                        );
                        DefineVertexAttrib(
                            gl,
                            this.AttribToIndexMap.VBTexCoords,
                            this.Mesh.TexCoordsBufferGPU!,
                            2,
                            gl.FLOAT,
                            2 * Float32Array.BYTES_PER_ELEMENT,
                        );
                        DefineVertexAttrib(
                            gl,
                            this.AttribToIndexMap.VBNormals,
                            this.Mesh.NormalsBufferGPU!,
                            3,
                            gl.FLOAT,
                            3 * Float32Array.BYTES_PER_ELEMENT,
                        );
                    }
                }
            }
            gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, null);
            gl.bindBuffer(gl.ARRAY_BUFFER, null);
            gl.bindVertexArray(null);
        }

        this.ShaderUpdate = new ShaderTF(
            gl,
            GetDestParticleUpdateShaderVS(parentTiles.NumTiles, parentTiles.TileSize, this.Desc),
            ParticleUpdatePS,
            GetBasePassUniformParametersList,
            ["outPosition", "outVelocity", "outAge"],
        );

        if (this.Desc.bSmokePath) {
            this.ShaderRender = new Shader(
                gl,
                GetShaderSourceBasePassSmokeParticleRenderVS(this.Desc),
                GetShaderSourceBasePassSmokeParticlePS(this.Desc),
                GetBasePassUniformParametersList,
            );
        } else {
            this.ShaderRender = new Shader(
                gl,
                GetShaderSourceBasePassParticleRenderVS(this.Desc),
                GetShaderSourceBasePassParticlePS(this.Desc),
                GetBasePassUniformParametersList,
            );
        }
    }
}
